import classes from "./DashGetInside.module.css";
import { useContext, useRef, useEffect, useState } from 'react';
import { getApiChara } from '../../../util/getApi';
import { getAuthToken } from '../../../util/auth';
import { toast } from "react-toastify";
import WaitingContext from '../../../store/waitingListContext';
import AuthContext from '../../../store/auth-context';
import Loader from "../../UI/Loader";
import { Form, useNavigation, Link, useNavigate } from "react-router-dom";
import { FaUser, FaIdCard, FaBirthdayCake, FaHashtag, FaMobileAlt, FaStethoscope, FaPrint, FaFileExport } from 'react-icons/fa';
import { calculateAgeDetailed } from '../../../util/ageCalculator';
import { useTranslation } from "react-i18next";
import { printPrescription } from "../Prescriptions/PrescriptionPrint";
import { exportPrescriptionPDF } from "../../../util/pdfExportUtil";
import specialityFields from "../../../util/specialityFields.json";

const GetInside = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const authCtx = useContext(AuthContext);
  const waitingPData = useContext(WaitingContext);
  const [isLoading, setIsLoading] = useState(false);
  const [patientAge, setPatientAge] = useState({ years: 0, months: 0, days: 0 });
  const [isPrintCasePaper, setIsPrintCasePaper] = useState(false);

  useEffect(() => {
    if (authCtx.hospitalData.PrintCasePaper) {
      setIsPrintCasePaper(authCtx.hospitalData.PrintCasePaper);
    }
  }, [authCtx.hospitalData.PrintCasePaper]);
  const wpatientInfo = waitingPData?.waitingPatient ? waitingPData?.waitingPatient?.patientId : null;

  const weightInput = useRef();
  const bpSys = useRef();
  const bpDys = useRef();
  const pulse = useRef();
  const temperature = useRef();
  const spo2 = useRef();

  // Dynamic fields (Standardized)
  const [lmp, setLmp] = useState(waitingPData?.waitingPatient?.lmp || waitingPData?.waitingPatient?.Lmp || "");
  const [edd, setEdd] = useState(waitingPData?.waitingPatient?.edd || waitingPData?.waitingPatient?.Edd || "");
  const [length, setLength] = useState(waitingPData?.waitingPatient?.length || "");
  const [hc, setHc] = useState(waitingPData?.waitingPatient?.hc || "");

  const speciality = (authCtx.hospitalData?.selectSpecialities || "general").toLowerCase();
  const visibleFields = specialityFields[speciality] || specialityFields["general"];

  // LMP to EDD calculation
  useEffect(() => {
    if (lmp) {
      const lmpDate = new Date(lmp);
      if (!isNaN(lmpDate.getTime())) {
        const eddDate = new Date(lmpDate);
        eddDate.setDate(eddDate.getDate() + 280);
        setEdd(eddDate.toISOString().split('T')[0]);
      }
    }
  }, [lmp]);
  const navigation = useNavigation();
  const isSubmitting = navigation.state === "submitting";
  
  const ageData = wpatientInfo?.dob ? calculateAgeDetailed(wpatientInfo?.dob) : { years: 0, months: 0, days: 0 };
  const { years, months, days } = ageData;

  useEffect(() => {
    setPatientAge({ years, months, days });
    if (!waitingPData?.waitingPatient) {
      return navigate('..');
    }
  }, [waitingPData?.waitingPatient, days, months, years, navigate]);
  const addPatientToCheckUpHandler = async (e) => {
    e.preventDefault();
    const eWeight = weightInput.current.value;
    const eBpSys = bpSys.current.value;
    const eBpDys = bpDys.current.value;
    const ePulse = pulse.current.value;
    const eTem = temperature.current.value;
    const eSpo2 = spo2.current.value;
    const checkUpDone = waitingPData.waitingPatient;
    checkUpDone.weight = eWeight;
    checkUpDone.bp1 = eBpSys;
    checkUpDone.bp2 = eBpDys;
    checkUpDone.pulse = ePulse;
    checkUpDone.temperature = eTem;
    checkUpDone.spo2 = eSpo2;
    checkUpDone.lmp = lmp || null;
    checkUpDone.edd = edd || null;
    checkUpDone.length = length || "";
    checkUpDone.hc = hc || "";
    checkUpDone.checkUpStatus = "SEND_INSIDE";
    checkUpDone.patientId.dob = null;
    const token = getAuthToken();
    const apiChar = getApiChara();
    const fetchApi = `${apiChar}/checkUp/addPatientIntoCheckUpList`;
    setIsLoading(true);
    try {
      const response = await fetch(fetchApi, {
        method: "post",
        body: JSON.stringify(checkUpDone),
        headers: {
          "Content-Type": "application/json",
          authorization: "Bearer " + token,
        },
      });
      if (!response.ok) {
        throw new Error("Network response was not ok");
      }
      const data = await response.json();

      if (isPrintCasePaper) {
        handlePrint();
      }

      navigate('..');
    } catch (error) {
      console.error(
        "There has been a problem with your fetch operation:",
        error
      );
      toast.error(t("Patient details not added"));
      navigate("/access-denied?errorCode=500&errorMessage=Server%20is%20offline&errorDescription=check%20your%20internet%20connection%20or");
    }
    finally {
      setIsLoading(false); // Hide loader after fetching data
    }
  };

  const getVitalsData = () => {
    return {
      weight: weightInput.current?.value || "-",
      bps: bpSys.current?.value || "-",
      bpd: bpDys.current?.value || "",
      pulse: pulse.current?.value || "-",
      temp: temperature.current?.value || "-",
      spo2: spo2.current?.value || "-",
      lmp,
      edd,
      length,
      hc
    };
  };

  const handlePrint = () => {
    const vitals = getVitalsData();
    printPrescription(waitingPData.waitingPatient, [], waitingPData.waitingPatient, vitals, new Date().toISOString(), authCtx, true);
  };

  const handleExport = () => {
    const vitals = getVitalsData();
    exportPrescriptionPDF({
      patientData: waitingPData.waitingPatient,
      medicines: [],
      checkedData: vitals,
      authCtx: authCtx,
      selectedDateTime: new Date().toISOString(),
      reportTitle: t("Case Paper")
    });
  };

  const today = new Date().toISOString().split('T')[0];
  const nineMonthsAgo = new Date();
  nineMonthsAgo.setMonth(nineMonthsAgo.getMonth() - 9);
  const minLmpDate = nineMonthsAgo.toISOString().split('T')[0];

  return (
    <>
      {isLoading && <Loader />}
      <div className="container">
        <div className="row">
          <div className="col-md-1">
          </div>
          <div className="col-md-10 col-sm-12 col-xs-12">
            <h4 className="textHead" style={{ color: 'var(--primary-color)', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '2px', marginBottom: '0' }}>
              <FaStethoscope className="me-2" style={{ verticalAlign: 'middle' }} />
              {t("Examination")}
            </h4>

            <div className={classes.patientInfoHeader}>
              <div className={classes.infoItem}>
                <FaHashtag /> <span>{t("SN")}: {waitingPData?.waitingPatient?.serialNo}</span>
              </div>
              <div className={classes.infoItem}>
                <FaIdCard /> <span>{t("ID")}: {wpatientInfo?.patientId}</span>
              </div>
              <div className={classes.infoItem}>
                <FaUser /> <span>{wpatientInfo?.firstName + " " + wpatientInfo?.middleName + " " + wpatientInfo?.lastName}</span>
              </div>
              <div className={classes.infoItem}>
                <FaBirthdayCake /> <span>{patientAge?.years}{t("Y")} {patientAge?.months}{t("M")} {patientAge?.days}{t("D")}</span>
              </div>
              <div className={classes.infoItem}>
                <FaMobileAlt /> <span>{wpatientInfo?.mobile}</span>
              </div>
            </div>

            <div className={classes.cardview}>
              <Form onSubmit={addPatientToCheckUpHandler} className={classes.form} style={{ marginTop: '0', borderRadius: '0 0 12px 12px' }}>
                <div className="row">
                  <div className="col-md-4 col-6">
                    <label className={classes.label} htmlFor="weight">{t("Weight (kg)")}</label>
                    <input id="weight" type="number" step="any" name="weight" ref={weightInput} placeholder="0.0" />
                  </div>
                  <div className="col-md-4 col-6">
                    <label className={classes.label} htmlFor="bpsystolic">{t("BP Systolic")}</label>
                    <input id="bpsystolic" type="number" step="any" name="bpsystolic" ref={bpSys} placeholder="120" />
                  </div>
                  <div className="col-md-4 col-6">
                    <label className={classes.label} htmlFor="bpdiastolic">{t("BP Diastolic")}</label>
                    <input id="bpdiastolic" type="number" step="any" name="bpdiastolic" ref={bpDys} placeholder="80" />
                  </div>
                  <div className="col-md-4 col-6">
                    <label className={classes.label} htmlFor="pulse">{t("Pulse (bpm)")}</label>
                    <input id="pulse" type="number" step="any" name="pulse" ref={pulse} placeholder="72" />
                  </div>
                  <div className="col-md-4 col-6">
                    <label className={classes.label} htmlFor="temperature">{t("Temp (°F)")}</label>
                    <input id="temperature" type="number" step="any" name="temperature" ref={temperature} placeholder="98.6" />
                  </div>
                  <div className="col-md-4 col-6">
                    <label className={classes.label} htmlFor="spo2">{t("SpO2 (%)")}</label>
                    <input id="spo2" type="number" step="any" name="spo2" ref={spo2} placeholder="98" />
                  </div>
                  {visibleFields.includes("lmp") && (
                    <div className="col-md-4 col-6">
                      <label className={classes.label} htmlFor="lmp">{t("LMP")}</label>
                      <input 
                        id="lmp" 
                        className="form-control" 
                        type="date" 
                        name="lmp" 
                        value={lmp} 
                        min={minLmpDate}
                        max={today}
                        onChange={(e) => setLmp(e.target.value)} 
                      />
                    </div>
                  )}
                  {visibleFields.includes("edd") && (
                    <div className="col-md-4 col-6">
                      <label className={classes.label} htmlFor="edd">{t("EDD")}</label>
                      <input id="edd" className="form-control" type="date" name="edd" value={edd} onChange={(e) => setEdd(e.target.value)} />
                    </div>
                  )}
                  {visibleFields.includes("length") && (
                    <div className="col-md-4 col-6">
                      <label className={classes.label} htmlFor="length">{t("Length")}</label>
                      <input id="length" className="form-control" type="text" name="length" value={length} onChange={(e) => setLength(e.target.value)} />
                    </div>
                  )}
                  {visibleFields.includes("hc") && (
                    <div className="col-md-4 col-6">
                      <label className={classes.label} htmlFor="hc">{t("Head Circ.")}</label>
                      <input id="hc" className="form-control" type="text" name="hc" value={hc} onChange={(e) => setHc(e.target.value)} />
                    </div>
                  )}
                </div>
                <div className={classes.actions} style={{ marginTop: '20px' }}>
                  <div className="d-flex align-items-center flex-wrap justify-content-center gap-3 w-100">
                    {/* Print Checkbox */}
                    <div className="form-check d-flex align-items-center gap-2 mb-0" style={{ paddingLeft: '0' }}>
                      <input
                        type="checkbox"
                        id="printCasePaper"
                        className="form-check-input"
                        checked={isPrintCasePaper}
                        onChange={(e) => setIsPrintCasePaper(e.target.checked)}
                        style={{ width: '20px', height: '20px', margin: '0 10px 0 0', cursor: 'pointer' }}
                      />
                      <label htmlFor="printCasePaper" className="form-check-label mb-0" style={{ fontSize: '13px', fontWeight: '700', color: '#444', cursor: 'pointer', textTransform: 'uppercase' }}>
                        {t("Print Case Paper")}
                      </label>
                    </div>

                    {/* Send Inside Button */}
                    <button
                      type="submit"
                      className={classes.premiumBtn}
                      style={{ padding: '0.6rem 2rem', fontSize: '12px', color: 'white' }}
                    >
                      {isSubmitting ? t("Submitting...") : t("Send Inside")}
                    </button>

                    {/* Print Button */}
                    <button
                      type="button"
                      onClick={handlePrint}
                      className="btn btn-primary d-flex align-items-center gap-2 text-white"
                      style={{ padding: '0.6rem 1.5rem', borderRadius: '8px', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', border: 'none', color: 'white' }}
                    >
                      <FaPrint style={{ color: 'white' }} /> {t("Print")}
                    </button>

                    {/* Export Button */}
                    <button
                      type="button"
                      onClick={handleExport}
                      className="btn btn-outline-info d-flex align-items-center gap-2 text-white"
                      style={{ padding: '0.6rem 1.5rem', borderRadius: '8px', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', backgroundColor: '#17a2b8', border: 'none' }}
                    >
                      <FaFileExport /> {t("Export")}
                    </button>

                    {/* Back Link */}
                    <Link
                      className="btn btn-secondary d-flex align-items-center justify-content-center"
                      to=".."
                      style={{ padding: '0.6rem 1.5rem', borderRadius: '8px', fontWeight: '700', fontSize: '12px', textTransform: 'uppercase', minWidth: '100px', backgroundColor: '#6c757d', border: 'none', color: 'white' }}
                    >
                      {t("Back")}
                    </Link>
                  </div>
                </div>
              </Form>
            </div>
          </div>
          <div className="col-md-1">
          </div>
        </div>
      </div>
    </>
  );
};
export default GetInside;
